<template>
  <!-- Banner Fixo no Topo -->
  <Transition name="slide-down">
    <div
      v-if="bannerVisible"
      class="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 shadow-2xl"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between py-3 sm:py-4">
          <!-- Ícone e Mensagem -->
          <div class="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
            <!-- Ícone Animado -->
            <div class="flex-shrink-0">
              <div class="relative">
                <DevicePhoneMobileIcon class="w-8 h-8 sm:w-10 sm:h-10 text-white animate-bounce-subtle" />
                <span class="absolute -top-1 -right-1 flex h-3 w-3">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                </span>
              </div>
            </div>
            
            <!-- Texto -->
            <div class="flex-1 min-w-0">
              <p class="text-white font-bold text-sm sm:text-base truncate">
                {{ $t('pwa.installMessage') }}
              </p>
              <p class="text-primary-100 text-xs sm:text-sm hidden sm:block">
                {{ $t('pwa.benefit1') }} • {{ $t('pwa.benefit2') }}
              </p>
              <p
                v-if="installPromptUnavailable"
                class="text-primary-50 text-[11px] sm:text-xs mt-1 sm:mt-2 max-w-4xl leading-snug"
              >
                {{ $t('pwa.manualInstallHint') }}
              </p>
              <p
                v-if="installPromptUnavailable"
                class="text-primary-50/90 text-[10px] sm:text-[11px] mt-0.5"
              >
                {{ $t('pwa.httpsHint') }}
              </p>
            </div>
          </div>
          
          <!-- Botões -->
          <div class="flex items-center space-x-2 sm:space-x-3 flex-shrink-0 ml-4">
            <!-- Botão Instalar -->
            <button
              @click="install"
              :disabled="installing"
              class="px-4 sm:px-6 py-2 sm:py-2.5 bg-white text-primary-600 font-bold text-sm sm:text-base rounded-lg hover:bg-primary-50 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              :title="installPromptUnavailable ? $t('pwa.manualInstallToast') : undefined"
            >
              <span v-if="installing" class="flex items-center space-x-2">
                <div class="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                <span class="hidden sm:inline">{{ $t('pwa.installing') }}</span>
              </span>
              <span v-else class="flex items-center space-x-2">
                <ArrowDownTrayIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{{ $t('pwa.installButton') }}</span>
              </span>
            </button>
            
            <!-- Botão Fechar -->
            <button
              @click="dismiss"
              class="p-2 text-white hover:bg-white/20 rounded-lg transition-colors duration-200"
              :aria-label="$t('common.close')"
            >
              <XMarkIcon class="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>
      </div>
      
      <!-- Barra de Progresso Decorativa -->
      <div class="h-1 bg-white/30">
        <div class="h-full bg-white animate-progress"></div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DevicePhoneMobileIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/vue/24/outline'
import {
  showInstallPrompt,
  isPWAInstalled,
  detectPWAInstalledAsync,
  markPWAAsInstalled
} from '@/registerServiceWorker'
import { useToast } from '@/stores/toast'

const { t } = useI18n()
const toast = useToast()
const route = useRoute()

/** Não competir com login / wizard / licença — só sugerir PWA na área “normal” da app */
const allowPwaOnRoute = computed(() => {
  const p = route.path
  if (p.startsWith('/auth')) return false
  if (p === '/install' || p.startsWith('/install/')) return false
  if (p === '/license-invalid') return false
  return true
})

const showBanner = ref(false)
const isInstalled = ref(isPWAInstalled())
const installing = ref(false)
const installPromptUnavailable = ref(false)

const bannerVisible = computed(
  () => showBanner.value && !isInstalled.value && allowPwaOnRoute.value
)

let showBannerTimeout = null

const isBannerDismissedCooling = () => {
  const dismissed = localStorage.getItem('pwaBannerDismissed')
  const dismissedTime = parseInt(dismissed, 10)
  const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
  return dismissed && dismissedTime >= oneDayAgo
}

const syncInstalledState = async () => {
  if (isInstalled.value || isPWAInstalled()) {
    isInstalled.value = true
    showBanner.value = false
    return true
  }
  const detected = await detectPWAInstalledAsync()
  if (detected) {
    isInstalled.value = true
    showBanner.value = false
    return true
  }
  return false
}

const scheduleBannerWithDelay = (delayMs) => {
  if (showBannerTimeout) {
    clearTimeout(showBannerTimeout)
    showBannerTimeout = null
  }
  if (!allowPwaOnRoute.value || isInstalled.value || isPWAInstalled()) {
    return
  }
  if (isBannerDismissedCooling()) {
    return
  }
  showBannerTimeout = setTimeout(async () => {
    showBannerTimeout = null
    if (!allowPwaOnRoute.value) {
      return
    }
    if (await syncInstalledState()) {
      return
    }

    showBanner.value = true
  }, delayMs)
}

const syncManualHint = () => {
  if (isInstalled.value || isPWAInstalled()) {
    installPromptUnavailable.value = false
    return
  }
  installPromptUnavailable.value = !window.deferredPrompt
}

const handlePWAAvailable = async () => {
  if (await syncInstalledState()) {

    return
  }

  installPromptUnavailable.value = false


  if (!allowPwaOnRoute.value) {

    return
  }

  scheduleBannerWithDelay(3000)
}

const handlePWAInstalled = () => {

  markPWAAsInstalled()
  isInstalled.value = true
  showBanner.value = false
  localStorage.removeItem('pwaBannerDismissed')
}

const install = async () => {
  if (!window.deferredPrompt) {
    toast.info(t('pwa.manualInstallToast'), {
      title: t('pwa.manualInstallToastTitle'),
      timeout: 10000,
    })
    return
  }

  installing.value = true
  try {
    const accepted = await showInstallPrompt()
    if (accepted) {
      showBanner.value = false
      isInstalled.value = true
      localStorage.removeItem('pwaBannerDismissed')
    }
  } catch (error) {
    console.error('Erro ao instalar PWA:', error)
  } finally {
    installing.value = false
  }
}

const dismiss = () => {
  showBanner.value = false
  // Salvar timestamp de quando foi dispensado
  localStorage.setItem('pwaBannerDismissed', Date.now().toString())

}

onMounted(async () => {


  if (await syncInstalledState()) {

    return
  }

  setTimeout(syncManualHint, 6000)

  // Fallback só se ainda não instalado e sem beforeinstallprompt (não insistir após instalar no Chrome)
  setTimeout(async () => {
    if (!allowPwaOnRoute.value) {
      return
    }
    if (await syncInstalledState()) {
      return
    }
    if (!showBanner.value && !isBannerDismissedCooling() && window.deferredPrompt) {

      showBanner.value = true
      syncManualHint()
    }
  }, 10000)

  window.addEventListener('pwaInstallAvailable', handlePWAAvailable)
  window.addEventListener('pwaInstalled', handlePWAInstalled)
})

watch(
  () => route.path,
  (path, prevPath) => {
    if (!allowPwaOnRoute.value) {
      if (showBannerTimeout) {
        clearTimeout(showBannerTimeout)
        showBannerTimeout = null
      }
      showBanner.value = false
      return
    }
    const leftAuth = prevPath && prevPath.startsWith('/auth') && !path.startsWith('/auth')
    if (leftAuth && !isInstalled.value && !isPWAInstalled() && !isBannerDismissedCooling()) {
      void syncInstalledState().then((installed) => {
        if (!installed && window.deferredPrompt) {
          scheduleBannerWithDelay(2500)
        }
      })
    }
  }
)

onUnmounted(() => {
  if (showBannerTimeout) {
    clearTimeout(showBannerTimeout)
  }
  window.removeEventListener('pwaInstallAvailable', handlePWAAvailable)
  window.removeEventListener('pwaInstalled', handlePWAInstalled)
})
</script>

<style scoped>
/* Slide down animation */
.slide-down-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-down-leave-active {
  transition: all 0.3s ease-in;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-50%);
}

/* Bounce sutil */
@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.animate-bounce-subtle {
  animation: bounce-subtle 2s ease-in-out infinite;
}

/* Barra de progresso */
@keyframes progress {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}

.animate-progress {
  animation: progress 30s linear infinite;
}
</style>

